/**
 * Keep the objects of a map's tree that did not change (engine design note,
 * docs/plans/2026-10-19-engine-interface-separation.md §3.2).
 *
 * The tree is built anew from the map after every change: every item was a
 * new object, even the 19,999 an edit did not touch. Here each item whose own
 * fields and children are unchanged gets its previous object back, so an
 * interface can redraw only what changed, and the change is described:
 * items added, removed, updated (their own fields), moved (another parent),
 * and the parents whose list of children changed (added, removed or
 * reordered; not a child's own change).
 *
 * Same object means the item's own data is unchanged. What it shows may
 * still depend on other things (tag definitions, a linked map's state).
 * The root is always a new object, as before: a view that redraws when the
 * tree changes still does after a change elsewhere (tag definitions).
 */

// root -> how many items the tree holds (to know, cheaply, that none was
// removed).
const counts = new WeakMap();

/** Every item of a tree by id, with its parent's id (the slow path). */
function indexOf(root) {
  const nodes = new Map();
  const parents = new Map();
  const walk = (node, parentId) => {
    nodes.set(node.id, node);
    parents.set(node.id, parentId);
    const children = node.children;
    if (children) for (let i = 0; i < children.length; i++) walk(children[i], node.id);
  };
  walk(root, null);
  return { nodes, parents };
}

function sameValue(a, b) {
  if (a === b) return true;
  if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) return false;
  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (!sameValue(a[i], b[i])) return false;
    return true;
  }
  if (Array.isArray(b)) return false;
  return sameFields(a, b, null);
}

/** Same own fields (`skip` aside), compared in depth. */
function sameFields(a, b, skip = "children") {
  let keys = 0;
  for (const key in b) {
    if (key === skip) continue;
    keys++;
    if (!sameValue(a[key], b[key])) return false;
  }
  for (const key in a) if (key !== skip) keys--;
  return keys === 0;
}

function sameList(a, b) {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

function sameIds(a, b) {
  if (!a || !b) return !a === !b;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i].id !== b[i].id) return false;
  return true;
}

/**
 * @param {Object|null} previous - the tree shown so far (same map), or null
 * @param {Object} next - the tree just built (its objects may be changed)
 * @returns {{tree: Object, changes: {added: string[], removed: string[],
 *   updated: string[], moved: string[], childrenChanged: string[]}}}
 */
export function reconcileTree(previous, next) {
  const changes = { added: [], removed: [], updated: [], moved: [], childrenChanged: [] };
  // Most changes leave the shape alone: the previous item is found at the
  // same place, without a lookup. The full index is built on the first item
  // found elsewhere (added, moved), at most once.
  let index = null;
  const lookup = (id) => {
    if (!previous) return null;
    if (!index) index = indexOf(previous);
    return index.nodes.get(id) || null;
  };
  let count = 0;
  let matched = 0;

  // The new tree is ours: its lists of children are filled in place.
  const visit = (node, counterpart, parentId) => {
    count++;
    let old = counterpart && counterpart.id === node.id ? counterpart : null;
    const inPlace = !!old;
    if (!old) old = lookup(node.id);
    const children = node.children;
    if (children) {
      const oldChildren = old?.children;
      for (let i = 0; i < children.length; i++) children[i] = visit(children[i], oldChildren?.[i], node.id);
    }
    let result = node;
    if (!old) {
      changes.added.push(node.id);
    } else {
      matched++;
      const sameOwn = sameFields(old, node);
      if (!sameOwn) changes.updated.push(node.id);
      if (!inPlace && index.parents.get(node.id) !== parentId) changes.moved.push(node.id);
      if (!sameIds(old.children, children)) changes.childrenChanged.push(node.id);
      if (sameOwn && sameList(old.children, children)) result = old;
    }
    return result;
  };

  let tree = visit(next, previous, null);
  // The root is always new (see above).
  if (tree === previous) tree = { ...tree };
  // An item not matched was removed (ids are unique in a map).
  if (previous && matched < (counts.get(previous) ?? Infinity)) {
    if (!index) index = indexOf(previous);
    const present = indexOf(tree).nodes;
    for (const id of index.nodes.keys()) if (!present.has(id)) changes.removed.push(id);
  }
  counts.set(tree, count);
  return { tree, changes };
}
